import type { WorkOrderStatus } from '@patchbay/ui'
import type { EmailReceipt } from '../../components/email-receipt'

export type BoardStatus = WorkOrderStatus

/**
 * A native work order (round 9: Patchbay owns WOs; `GET /api/v1/work-orders`, WorkOrderSummaryOut). Money fields
 * are absent for roles that may not see them (the serializer drops them), never null-as-zero.
 */
export interface WorkOrderDto {
  id: string
  display_number?: string | null
  legacy_conduit_wo_id?: string | null
  case_id?: string | null
  case_number?: string | null
  case_visit_number?: number | null
  case_work_order_count?: number | null
  case_scoped_label?: string | null
  customer_id?: string | null
  customer_display?: string | null
  desk_ticket_id?: string | null
  site_address?: string | null
  status: Exclude<WorkOrderStatus, 'paid'>
  kind?: string | null
  assignee_kind?: 'contractor' | 'internal_tech'
  assignee_user_id?: string | null
  contractor_id?: string | null
  contractor_display?: string | null
  scheduled_start?: string | null
  scheduled_end?: string | null
  time_precision?: 'exact' | 'morning' | 'afternoon' | 'allday' | null
  po_number?: string | null
  books_po_id?: string | null
  books_po_number?: string | null
  minutes_total?: number | null
  tasks_done?: number
  tasks_total?: number
  checked_in_at?: string | null
  checked_out_at?: string | null
  requires_photos?: boolean
  requires_signoff?: boolean
  sent_at?: string | null
  submitted_at?: string | null
  approved_at?: string | null
  invoice_attached?: boolean
  public_link_active?: boolean
  public_link_expires_at?: string | null
  created_at?: string
  updated_at?: string
  // money (absent for techs)
  pricing_basis?: 't_and_m' | 'quote_fixed' | 'quote_min_hours_tm' | 'none' | null
  min_hours?: string | number | null
  hourly_rate?: string | number | null
  travel_amount?: string | number | null
  fixed_amount?: string | number | null
  po_amount?: string | null
  currency?: string | null
  payment_status?: 'n/a' | 'due' | 'scheduled' | 'paid' | null
  /** Older board rows only (kept so a cached board still renders). */
  invoice_number?: string | null
  payment_notice_sent?: boolean
  po_label?: string | null
  invoices?: string[]
}

/** `GET /api/v1/work-orders/{id}` (WorkOrderDetailOut). */
export interface WorkOrderDetailDto extends WorkOrderDto {
  scope_text?: string | null
  site_contact_name?: string | null
  site_contact_phone?: string | null
  access_notes?: string | null
  returned_reason?: string | null
  revoked_reason?: string | null
  tasks: { id: string; position: number; text: string; done_at?: string | null; done_by_kind?: string | null }[]
  visits: {
    id: string
    checked_in_at: string
    checked_out_at?: string | null
    by_kind: string
    crew_count?: number
    crew_names?: string[]
    entered_later?: boolean
    review_state?: string | null
  }[]
  time: {
    id: string
    minutes: number
    work_date: string
    note?: string | null
    by_kind: string
    source: string
    crew_count?: number
    visit_id?: string | null
    entered_later?: boolean
    review_state?: string | null
  }[]
  files: { id: string; role: string; filename: string; content_type: string; size_bytes: number; created_at: string }[]
  missing: string[]
  dispatch_email_state?: 'unconfirmed' | null
  email_receipt?: EmailReceipt | null
  resend_available?: boolean
  crm_quote_ref?: string | null
  doc_questions?: {
    state: 'unasked' | 'ready' | 'partial' | 'unreachable' | 'none'
    note?: string | null
    frozen_at?: string | null
    questions: {
      id: string
      position: number
      prompt: string
      input_type: string
      answer_kind?: string | null
      answer_text?: string | null
      answer_reason?: string | null
      writeback_outcome?: string | null
    }[]
  }
}

export interface WorkOrderEditInput {
  contractor_id?: string | null
  contractor_display?: string | null
  scope_text?: string | null
  scheduled_start?: string | null
  scheduled_end?: string | null
  time_precision?: WorkOrderDto['time_precision']
  site_address?: string | null
  pricing?: {
    basis: NonNullable<WorkOrderDto['pricing_basis']>
    hourly_rate?: string | number | null
    travel_amount?: string | number | null
    fixed_amount?: string | number | null
    min_hours?: string | number | null
    currency?: string
  }
  visit_id?: string
  checked_in_at?: string | null
  checked_out_at?: string | null
  time_row_id?: string
  time_minutes?: number
  time_work_date?: string
  time_note?: string | null
  visit_crew_count?: number
  visit_crew_names?: string[]
  time_crew_count?: number
  approve_entered_later?: boolean
}

export interface WorkOrderPage {
  items: WorkOrderDto[]
  next_cursor?: string | null
}

export interface BookingDto {
  id: string
  case_id?: string | null
  resource_kind: 'agent' | 'contractor'
  resource_display_internal?: string | null
  starts_at: string
  ends_at?: string | null
  time_precision: 'exact' | 'morning' | 'afternoon' | 'allday'
  invite_suppressed: boolean
  invite_suppressed_reason?: string | null
  work_order_id?: string | null
}

export interface BoardDto {
  columns: { status: BoardStatus; cards: WorkOrderDto[] }[]
  week: BookingDto[]
  /** The week strip reads eweetools bookings; its outage costs only the strip, never the work orders. */
  week_state: 'ok' | 'unreachable'
  /** True when the board stopped paging before the end (more than BOARD_PAGE_CAP work orders). */
  truncated: boolean
}

export interface CardDto {
  id: string
  kind: string
  title?: string | null
  audience: 'internal' | 'customer' | 'contractor' | 'vendor' | 'unclassified'
  executor: 'riley' | 'patchbay'
  impact_text: string
  preview: Record<string, unknown>
  payload: Record<string, unknown>
  scrub_report?: Record<string, number> | null
  state: 'proposed' | 'confirmed' | 'edited_confirmed' | 'dismissed' | 'expired' | 'failed'
  can_confirm: boolean
  result?: Record<string, unknown> | null
  decided_at?: string | null
}

export interface PurchaseOrderDto {
  id: string
  kind: 'contractor' | 'hardware'
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'issued' | 'partially_received' | 'received' | 'billed' | 'cancelled'
  status_text: string
  books_state: 'ok' | 'books_scope_missing' | 'books_unavailable' | 'books_approval_not_enabled'
  books_state_text?: string | null
  books_po_number?: string | null
  books_po_id?: string | null
  books_org_id?: string | null
  /** Zoho Books page. Absent until a Books record id is stored. */
  books_url?: string | null
  reference_number: string
  work_order_id?: string | null
  downstream_state: 'waiting' | 'running' | 'sent' | 'unconfirmed' | 'failed' | 'none'
  downstream_detail?: string | null
  last_polled_at?: string | null
  amount?: string | null
  currency?: string | null
}

export interface BoardFilters {
  contractorId?: string
  customerId?: string
  dateFrom?: string
  dateTo?: string
}

export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

function cookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const prefix = `${name}=`
  const value = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  return value ? decodeURIComponent(value.slice(prefix.length)) : undefined
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  const bodyIsForm = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (init.body !== undefined && !bodyIsForm && !headers.has('content-type')) headers.set('content-type', 'application/json')
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = cookie('pb_csrf')
    if (csrf) headers.set('x-csrf-token', csrf)
  }
  const response = await fetch(path, { ...init, method, headers, credentials: 'same-origin' })
  if (!response.ok) {
    let body: { detail?: string; code?: string } | undefined
    try {
      body = (await response.json()) as { detail?: string; code?: string }
    } catch {
      body = undefined
    }
    throw new ApiError(body?.detail ?? `Request failed (${response.status})`, response.status, body?.code)
  }
  if (response.status === 204) return undefined as T
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) return (await response.text()) as T
  return (await response.json()) as T
}

function query(params: Record<string, string | undefined>): string {
  const out = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value) out.set(key, value)
  const encoded = out.toString()
  return encoded ? `?${encoded}` : ''
}

const BOARD_PAGE_SIZE = 200
const BOARD_PAGE_CAP = 5
const BOARD_ORDER: BoardStatus[] = ['draft', 'sent', 'in_progress', 'submitted', 'returned', 'approved', 'expired', 'revoked', 'paid']

/** Approved and paid is its own column; everything else is the WO's own status. */
export function boardStatusOf(wo: WorkOrderDto): BoardStatus {
  return wo.status === 'approved' && wo.payment_status === 'paid' ? 'paid' : wo.status
}

export function groupBoard(items: WorkOrderDto[], filters: BoardFilters = {}): BoardDto['columns'] {
  const rows = items.filter((wo) => !filters.customerId || wo.customer_id === filters.customerId)
  return BOARD_ORDER.map((status) => ({ status, cards: rows.filter((wo) => boardStatusOf(wo) === status) }))
}

export interface BoardTestSource {
  workOrders: (filters: { contractorId?: string; caseId?: string }) => Promise<{ items: WorkOrderDto[]; truncated: boolean }>
  week: (filters: BoardFilters) => Promise<{ week: BookingDto[]; state: BoardDto['week_state'] }>
}

let testDefault: BoardTestSource | null = null

/** src/test/setup.ts installs fixture-backed reads for every vitest run; production code imports no fixtures module. */
export function installBoardTestDefault(source: BoardTestSource | null): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Board test default is test-only')
  testDefault = source
}

export async function fetchWorkOrders(
  filters: { contractorId?: string; caseId?: string } = {},
): Promise<{ items: WorkOrderDto[]; truncated: boolean }> {
  if (testDefault) return testDefault.workOrders(filters)
  const items: WorkOrderDto[] = []
  let cursor: string | undefined
  for (let page = 0; page < BOARD_PAGE_CAP; page += 1) {
    const out = await apiRequest<WorkOrderPage>(
      `/api/v1/work-orders${query({ contractor_id: filters.contractorId, case_id: filters.caseId, cursor, limit: String(BOARD_PAGE_SIZE) })}`,
    )
    items.push(...out.items)
    if (!out.next_cursor) return { items, truncated: false }
    cursor = out.next_cursor
  }
  return { items, truncated: true }
}

interface DispatchProjection {
  columns?: { cards: Pick<WorkOrderDto, 'id' | 'payment_notice_sent' | 'po_label' | 'invoices'>[] }[]
  week?: BookingDto[]
}

async function fetchProjection(
  filters: BoardFilters,
): Promise<{ week: BookingDto[]; state: BoardDto['week_state']; metadata: Map<string, Partial<WorkOrderDto>> }> {
  if (testDefault) {
    const fixture = await testDefault.week(filters)
    return { ...fixture, metadata: new Map() }
  }
  try {
    const out = await apiRequest<DispatchProjection>(
      `/api/v1/dispatch/board${query({ contractor_id: filters.contractorId, date_from: filters.dateFrom, date_to: filters.dateTo })}`,
    )
    const metadata = new Map<string, Partial<WorkOrderDto>>()
    for (const column of out.columns ?? []) {
      for (const card of column.cards) {
        metadata.set(card.id, {
          payment_notice_sent: card.payment_notice_sent,
          po_label: card.po_label,
          invoices: card.invoices,
        })
      }
    }
    return { week: out.week ?? [], state: 'ok', metadata }
  } catch {
    return { week: [], state: 'unreachable', metadata: new Map() }
  }
}

/** The dispatch board over native work orders (round 9), plus the week strip of bookings. */
export async function fetchBoard(filters: BoardFilters = {}): Promise<BoardDto> {
  const [wos, projection] = await Promise.all([fetchWorkOrders({ contractorId: filters.contractorId }), fetchProjection(filters)])
  const items = wos.items.map((workOrder) => ({ ...workOrder, ...projection.metadata.get(workOrder.id) }))
  return {
    columns: groupBoard(items, filters),
    week: projection.week,
    week_state: projection.state,
    truncated: wos.truncated,
  }
}

export async function fetchWorkOrder(workOrderId: string): Promise<WorkOrderDetailDto> {
  return apiRequest<WorkOrderDetailDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}`)
}

export async function requestEditWorkOrder(workOrderId: string, body: WorkOrderEditInput): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/edit`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function fetchContractorFirms(): Promise<{ conduit_contractor_id: string; name: string }[]> {
  return apiRequest<{ conduit_contractor_id: string; name: string }[]>('/api/v1/portal/admin/firms')
}

/** A 5-minute download link; never logged, never kept. */
export async function fetchWorkOrderFileUrl(workOrderId: string, fileId: string): Promise<string> {
  const out = await apiRequest<{ url: string }>(
    `/api/v1/work-orders/${encodeURIComponent(workOrderId)}/files/${encodeURIComponent(fileId)}/url`,
  )
  return out.url
}

export async function fetchWorkOrderPreview(workOrderId: string): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/preview`)
}

/** Approve and return only propose frozen cards. The work order changes after the card is confirmed. */
/** Read Conduit and freeze the documentation questions. Does not write answers back. */
export async function prepareDocQuestions(workOrderId: string): Promise<WorkOrderDetailDto> {
  return apiRequest<WorkOrderDetailDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/doc-questions/prepare`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

/** The only staff action that writes documentation answers to Conduit. Approving the work order does not. */
export async function approveDocQuestions(workOrderId: string): Promise<WorkOrderDetailDto> {
  return apiRequest<WorkOrderDetailDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/doc-questions/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function requestApprove(workOrderId: string): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function requestReturn(workOrderId: string, reason: string): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/return`, {
    method: 'POST',
    body: JSON.stringify({ comment: reason }),
  })
}

export async function requestMarkPaid(workOrderId: string, booksBillRef: string, paidOn: string): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/mark-paid`, {
    method: 'POST',
    body: JSON.stringify({ books_bill_ref: booksBillRef, paid_on: paidOn }),
  })
}

export async function fetchPurchaseOrders(workOrderId: string): Promise<PurchaseOrderDto[]> {
  return apiRequest<PurchaseOrderDto[]>(`/api/v1/purchase-orders?work_order_id=${encodeURIComponent(workOrderId)}`)
}

export async function refreshPurchaseOrder(purchaseOrderId: string): Promise<PurchaseOrderDto> {
  return apiRequest<PurchaseOrderDto>(`/api/v1/purchase-orders/${encodeURIComponent(purchaseOrderId)}/refresh`, { method: 'POST' })
}

export async function requestCancelPurchaseOrder(purchaseOrderId: string, reason: string): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/purchase-orders/${encodeURIComponent(purchaseOrderId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function requestResendDispatch(workOrderId: string, reason: string): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/resend-dispatch`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function revokeWorkOrder(workOrderId: string, reason: string): Promise<WorkOrderDetailDto> {
  return apiRequest<WorkOrderDetailDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function requestPaymentNotice(
  workOrderId: string,
  invoiceNumber: string | null,
  scheduledOn: string | null,
): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/work-orders/${encodeURIComponent(workOrderId)}/payment-notice`, {
    method: 'POST',
    body: JSON.stringify({ invoice_number: invoiceNumber, scheduled_on: scheduledOn }),
  })
}

const CONFIRM_TIMEOUT_MS = 30_000
const CONFIRM_TIMEOUT_MESSAGE = 'Confirm did not finish. Nothing may have been sent. Retry.'

export async function confirmCard(cardId: string): Promise<CardDto> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(new DOMException(CONFIRM_TIMEOUT_MESSAGE, 'TimeoutError')), CONFIRM_TIMEOUT_MS)
  try {
    return await apiRequest<CardDto>(`/api/v1/cards/${encodeURIComponent(cardId)}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ edited_payload: null }),
      signal: ctl.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiError(CONFIRM_TIMEOUT_MESSAGE, 504, 'confirm_timeout')
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}

export async function dismissCard(cardId: string, reason?: string | null): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/cards/${encodeURIComponent(cardId)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason?.trim() || null }),
  })
}
